/**
 * "Vim do prontuário" — a volta para a aba de onde o operador saiu.
 *
 * Agendar a partir do prontuário leva a uma CADEIA de telas: agendamento → cobrança
 * (quando a forma não foi fechada) → detalhe do atendimento → guia em PDF. Cada uma
 * navega por conta própria, e nenhuma sabe da anterior.
 *
 * Por isso a origem NÃO viaja na URL: teria de ser repassada em cada `navigate` da
 * cadeia, e bastaria um esquecido para o operador ficar sem volta. Fica no
 * `sessionStorage`, e um botão fixo no layout a lê de qualquer tela.
 *
 * `sessionStorage` (não `localStorage`) de propósito: é por ABA. Dois pacientes
 * abertos em duas abas não se misturam, e fechar a aba encerra o fluxo.
 */

const KEY = 'prontuario-origin'

/**
 * Validade da origem. Fluxo abandonado não pode virar um botão que persegue o
 * operador para sempre — some sozinho no dia seguinte.
 */
const TTL_MS = 12 * 60 * 60 * 1000

export interface ProntuarioOrigin {
    /** Rota de volta — sempre uma aba do prontuário (exames ou procedimentos). */
    path: string
    /** Nome do paciente, para o botão dizer para onde volta. */
    clientName: string
    /** Quando a origem foi gravada (epoch ms). */
    at: number
}

export function setProntuarioOrigin(
    path: string,
    clientName: string,
): void {
    try {
        const origin: ProntuarioOrigin = {
            path,
            clientName,
            at: Date.now(),
        }
        sessionStorage.setItem(KEY, JSON.stringify(origin))
    } catch {
        // Aba anônima com storage bloqueado: perde-se só o atalho de volta.
    }
}

export function getProntuarioOrigin(): ProntuarioOrigin | null {
    try {
        const raw = sessionStorage.getItem(KEY)
        if (!raw) return null
        const origin = JSON.parse(raw) as ProntuarioOrigin
        if (!origin?.path || !origin?.at) return null
        if (Date.now() - origin.at > TTL_MS) {
            sessionStorage.removeItem(KEY)
            return null
        }
        return origin
    } catch {
        return null
    }
}

export function clearProntuarioOrigin(): void {
    try {
        sessionStorage.removeItem(KEY)
    } catch {
        // Idem: nada a fazer, e falhar aqui não pode quebrar a navegação.
    }
}
